import { type ReactNode, useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_MAP = { sm: '380px', md: '480px', lg: '640px' };

export default function Modal({ isOpen, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 120,
      background: 'rgba(20, 35, 63, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '8px',
    }}>
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} />
      <div style={{
        position: 'relative',
        maxWidth: SIZE_MAP[size],
        width: '100%',
        maxHeight: 'calc(100dvh - 16px)',
        background: 'var(--paper, #fff)',
        borderRadius: '14px',
        boxShadow: '0 30px 60px -20px rgba(20,35,63,.5)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {title && (
          <div style={{
            flexShrink: 0,
            background: 'var(--navy, #1a2540)', color: '#fff',
            padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
          }}>
            <h3 style={{ fontSize: '15px', fontWeight: 800, flex: 1, margin: 0, wordBreak: 'break-word', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
            <button onClick={onClose} style={{
              width: '28px', height: '28px', borderRadius: '7px',
              background: 'rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', color: '#fff', cursor: 'pointer', border: 'none', flexShrink: 0,
            }}>✕</button>
          </div>
        )}
        <div style={{
          flex: 1, overflowY: 'auto', minHeight: 0,
          padding: '20px',
          WebkitOverflowScrolling: 'touch',
        }}>{children}</div>
        {footer && (
          <div style={{
            flexShrink: 0,
            padding: '12px 20px',
            borderTop: '1px solid var(--line, #e5e7eb)',
            display: 'flex', justifyContent: 'flex-end', gap: '9px', flexWrap: 'wrap',
            background: 'var(--paper, #fff)',
          }}>{footer}</div>
        )}
      </div>
    </div>
  );
}
